import React, { useMemo } from "react"
import { useDispatch } from "react-redux"
import { graphql, useLazyLoadQuery, useSubscription } from "react-relay"
import { toast } from "react-toastify"
import { match } from "ts-pattern"

import styles from "./ExternalProjectBulkImport.module.css"

import type { ExternalProjectBulkImportQuery } from "./__generated__/ExternalProjectBulkImportQuery.graphql"

import { pluralize } from "~/helpers/plural"
import { integrationsUrl } from "~/helpers/routes"

import { changeActivePage } from "~/common/ActivePage/ActivePage.slice"
import { ModalBody, ModalFooter, ModalFormWrapper } from "~/common/ModalForm"
import Button from "~/common/buttons/Button"
import { InfoCircle } from "~/common/react-icons"

import { bulkImportExtProjectListRelay } from "~/mutations/Project"

import { IntegrationImportProjectsTable } from "~/Integrations/IntegrationImportTable/IntegrationImportProjectsTable"
import IntegrationSyncDescription from "~/Integrations/IntegrationSyncDescription"
import { LazyIntegrationSyncHistory } from "~/Integrations/IntegrationSyncHistory"
import { usePermissions } from "~/Permissions/usePermissions"
import { useEffectSkipFirstRender } from "~/hooks/useEffectSkipFirstRender"

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EXTERNAL_PROJECTS_BULK_IMPORT_FRAGMENT = graphql`
  fragment ExternalProjectBulkImport_integration on integration {
    id
    state
    active
    last_sync_at

    integration_syncs(order_by: { id: desc }, limit: 1) {
      id
      state
      started_at
      finished_at
      info_messages
    }

    ext_projects(
      where: { active: { _eq: true }, sync: { _eq: false } }
      order_by: { name: asc, id: asc }
    ) {
      id
      ...IntegrationImportProjectsTable_ext_project
    }

    ...IntegrationSyncDescription_integration
  }
`
const EXTERNAL_PROJECTS_BULK_IMPORT_QUERY = graphql`
  query ExternalProjectBulkImportQuery($integrationId: Int!) {
    integration_by_pk(id: $integrationId) {
      id
      ext_projects(
        where: { active: { _eq: true }, sync: { _eq: false } }
        order_by: { name: asc, id: asc }
      ) {
        id
        ...IntegrationImportProjectsTable_ext_project
      }

      ...ExternalProjectBulkImport_integration @relay(mask: false)
    }
  }
`

const EXTERNAL_PROJECTS_BULK_IMPORT_SUBSCRIPTION = graphql`
  subscription ExternalProjectBulkImportSubscription($integrationId: Int!) {
    integration_by_pk(id: $integrationId) {
      ...ExternalProjectBulkImport_integration
    }
  }
`

type Modal =
  | "importingProjects"
  | "syncingProjects"
  | "syncingProjectsComplete"
  | "syncHistory"

type Props = {
  integrationId: number

  onClose?: () => void
  onNavigateBack?: () => void
}

const ExternalProjectBulkImport = (props: Props) => {
  const { integrationId, onClose, onNavigateBack } = props

  const dispatch = useDispatch()
  const { can, subject } = usePermissions()

  const [checkedSet, setCheckedSet] = React.useState<Set<number>>(new Set())
  const [loading, setLoading] = React.useState(false)
  const [modal, setModal] = React.useState<Modal | undefined>(
    "importingProjects",
  )

  const numSelected = checkedSet.size
  const canImport = numSelected > 0
  const canViewIntegrations = can("view", subject("Integration"))

  const data = useLazyLoadQuery<ExternalProjectBulkImportQuery>(
    EXTERNAL_PROJECTS_BULK_IMPORT_QUERY,
    { integrationId },
    { fetchPolicy: "store-and-network" },
  )

  useSubscription(
    useMemo(
      () => ({
        variables: {
          integrationId: integrationId,
        },
        subscription: EXTERNAL_PROJECTS_BULK_IMPORT_SUBSCRIPTION,
      }),
      [integrationId],
    ),
  )

  const lastIntegrationSync = data.integration_by_pk?.integration_syncs?.[0]
  const syncingState: Modal | undefined = (() => {
    if (lastIntegrationSync && lastIntegrationSync.finished_at == null) {
      return "syncingProjects" as const
    } else if (lastIntegrationSync && lastIntegrationSync.finished_at) {
      return "syncingProjectsComplete" as const
    }

    return undefined
  })()

  // Sync the modal state with the syncing state from the subscription.
  // If you have this sync history open, ignore the change.
  useEffectSkipFirstRender(() => {
    if (modal !== "syncHistory") {
      setModal(syncingState)
    }
  }, [syncingState, modal])

  const handleSyncingProjectsOpen = () => setModal("syncingProjects")
  const handleSyncHistoryOpen = () => setModal("syncHistory")
  const handleSyncHistoryClose = () => setModal(syncingState)

  const handleImport = async () => {
    setLoading(true)

    const extProjectIdList = Array.from(checkedSet)
    try {
      await bulkImportExtProjectListRelay({
        input: {
          integrationId,
          extProjectIdList,
        },
      })

      handleSyncingProjectsOpen()
    } catch (error) {
      console.error("Failed to import external projects", error)
      toast.error("Failed to import external projects")
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setLoading(false)
    onClose?.()
    setTimeout(() => {
      dispatch(changeActivePage("projects"))
      // Only reload if the sync has been completed.
      if (modal === "syncingProjectsComplete") {
        window.location.reload()
      }
      setModal("importingProjects")
    }, 500)
  }

  return (
    <ModalFormWrapper
      wide
      headerTitle="Select Projects to Import and Create"
      onClose={handleClose}
    >
      <ModalBody style={{ maxWidth: "1200px" }}>
        {match(modal)
          .when(
            (s) => s === "syncingProjects" || s === "syncingProjectsComplete",
            (s) => {
              const title =
                s === "syncingProjects"
                  ? "Just a few more minutes!"
                  : "All done!"

              const description =
                s === "syncingProjects" ? (
                  <>
                    We are importing your projects in the background. <br />
                    Feel free to{" "}
                    {canViewIntegrations ? (
                      <>
                        check back later on the{" "}
                        <a
                          href={integrationsUrl()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          integrations page
                        </a>
                      </>
                    ) : (
                      "close this window"
                    )}
                    , you won't lose any progress.
                  </>
                ) : (
                  <>Your projects have been imported.</>
                )

              return (
                <IntegrationSyncDescription
                  integration={data.integration_by_pk}
                  title={title}
                  description={description}
                  onOpenSyncHistory={handleSyncHistoryOpen}
                />
              )
            },
          )
          .with("importingProjects", () => (
            <IntegrationImportProjectsTable
              extProjectKey={data.integration_by_pk.ext_projects}
              checkedSet={checkedSet}
              onChange={setCheckedSet}
            />
          ))
          .with("syncHistory", () => (
            <LazyIntegrationSyncHistory integration={data.integration_by_pk} />
          ))
          .otherwise(() => null)}
      </ModalBody>

      <ModalFooter className={styles.modalFooter}>
        {match(modal)
          .when(
            (s) => s === "syncingProjects" || s === "syncingProjectsComplete",
            () => <Button text="Done" outlined={false} onClick={handleClose} />,
          )
          .with("syncHistory", () => (
            <Button
              text="Back"
              outlined={false}
              onClick={handleSyncHistoryClose}
            />
          ))
          .with("importingProjects", () => (
            <>
              {numSelected > 0 ? (
                <div className={styles.selectedCount}>
                  <InfoCircle /> {numSelected}{" "}
                  {pluralize(numSelected, "Project")} selected to import
                </div>
              ) : (
                <div />
              )}

              <div>
                {onNavigateBack ? (
                  <Button text="Back" onClick={onNavigateBack} />
                ) : null}
                <Button
                  text="Import"
                  onClick={handleImport}
                  loading={loading}
                  outlined={false}
                  disabled={!canImport}
                />
              </div>
            </>
          ))
          .otherwise(() => null)}
      </ModalFooter>
    </ModalFormWrapper>
  )
}

export { ExternalProjectBulkImport }
